import { Component, Inject, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { ClipboardModule, Clipboard } from '@angular/cdk/clipboard';
import { PhotoboothService } from '../../services/photobooth.service';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-session-details-modal',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatSnackBarModule, ClipboardModule],
  templateUrl: './session-details-modal.component.html',
  styleUrls: ['./session-details-modal.component.css']
})
export class SessionDetailsModalComponent implements OnInit {
  private photoboothService = inject(PhotoboothService);
  private snackBar = inject(MatSnackBar);
  private clipboard = inject(Clipboard);
  
  sessions: any[] = [];
  isLoading = true;
  expandedSession: any = null;
  
  sortColumn: string = 'Timestamp';
  sortDirection: 'desc' | 'asc' = 'desc';

  constructor(
    public dialogRef: MatDialogRef<SessionDetailsModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
      deviceId: string;
      storeId: string;
      date: string;
      endDate?: string;
      formattedDate: string;
      isKonoboxBrand?: boolean;
    }
  ) {}

  async ngOnInit() {
    try {
      let rawSessions: any[] = [];
      if (this.data.endDate && this.data.endDate !== this.data.date) {
        rawSessions = await this.photoboothService.getDeviceSessionsForDateRange(this.data.deviceId, this.data.date, this.data.endDate);
      } else {
        rawSessions = await this.photoboothService.getDeviceSessionsForDate(this.data.deviceId, this.data.date);
      }

      this.sessions = rawSessions.map(s => {
        // PaymentMethod mapping (AC-7)
        let payMethod = s.PaymentMethod;
        if (!payMethod) {
          const pType = (s.payType || s.payMethod || '').toString().toUpperCase();
          if (pType === 'COIN' || pType === 'BILL' || pType === 'CASH') {
            payMethod = 'Cash';
          } else if (pType === 'CARD') {
            payMethod = 'Card';
          } else if (pType === 'FREE' || pType === 'BYPASS' || s.isSkip) {
            payMethod = 'Bypass';
          } else {
            payMethod = 'Unknown';
          }
        }

        // AmountPaid mapping (AC-7)
        const amountPaid = s.AmountPaid !== undefined ? s.AmountPaid 
          : (s.TotalPaid !== undefined ? s.TotalPaid 
          : (s.paid !== undefined ? s.paid 
          : (s.amount !== undefined ? (Number(s.amount) || 0) : 0)));

        return {
          ...s,
          PaymentMethod: payMethod,
          AmountPaid: amountPaid
        };
      });

      this.applySort();
    } catch (error) {
      console.error('Error fetching sessions:', error);
    } finally {
      this.isLoading = false;
    }
  }

  sortBy(column: string) {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.applySort();
  }

  applySort() {
    this.sessions.sort((a, b) => {
      let valA: any = '';
      let valB: any = '';
      
      // Handle special cases for nested properties
      if (this.sortColumn === 'Timestamp') {
        valA = a.Timestamp?.seconds || a.CreationTime?.seconds || 0;
        valB = b.Timestamp?.seconds || b.CreationTime?.seconds || 0;
      } else if (this.sortColumn === 'Animefied') {
        valA = (a.AnimefiedGenerated || 0) + (a.AnimefiedUsed || 0);
        valB = (b.AnimefiedGenerated || 0) + (b.AnimefiedUsed || 0);
      } else {
        valA = a[this.sortColumn] ?? '';
        valB = b[this.sortColumn] ?? '';
      }

      if (typeof valA === 'string') valA = valA.toLowerCase();
      if (typeof valB === 'string') valB = valB.toLowerCase();
      
      if (valA < valB) return this.sortDirection === 'asc' ? -1 : 1;
      if (valA > valB) return this.sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }

  getClaimUrl(sessionId: string): string {
    // Standard path format: https://portal.kphotolavie.com/session/<sessionId>?machine=<machineId>&date=<date>
    return `${environment.userPortalUrl}/session/${sessionId}?machine=${this.data.deviceId}&date=${this.data.date}`;
  }

  copySessionId(sessionId: string) {
    this.clipboard.copy(sessionId);
    this.snackBar.open('Copied', '', { 
      duration: 2000, 
      horizontalPosition: 'center', 
      verticalPosition: 'bottom' 
    });
  }

  toggleRow(session: any) {
    if (this.expandedSession === session) {
      this.expandedSession = null;
    } else {
      this.expandedSession = session;
      if (!session.frameImageUrl && !session.isLoadingFrame) {
        this.loadFrameImage(session);
      }
    }
  }

  async loadFrameImage(session: any) {
    if (!session.Theme || !session.ThemeFrameGroupId) return;
    session.isLoadingFrame = true;
    try {
      const result = await this.photoboothService.resolveFrameImageUrl(this.data.deviceId, session.Theme, session.ThemeFrameGroupId, session.FrameId);
      session.frameImageUrl = result.url;
    } catch (e) {
      console.error('Failed to load frame image', e);
    } finally {
      session.isLoadingFrame = false;
    }
  }

  close() {
    this.dialogRef.close();
  }
}
